import * as React from 'react';
import { useRecordContext } from 'react-admin';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';

import CustomStatusField from './CustomStatusField';
import { CustomDBDeleteButton } from '../controls';
import CustomDBDiscontinueLicenseButton from './CustomDBDiscontinueLicenseButton';
import CustomDiscontinueLicenseMenu from './CustomDiscontinueLicenseMenu';

interface Props {
    source: string;
    label?: string;
    sortable?: boolean;
    userGuid?: string;
    schools?: any[];
}

const convertDateToString = (value: Date) => {
    if (!(value instanceof Date) || isNaN(value.getDate())) return '';
    const pad = '00';
    const yyyy = value.getFullYear().toString();
    const MM = (value.getMonth() + 1).toString();
    const dd = value.getDate().toString();
    return `${(pad + dd).slice(-2)}.${(pad + MM).slice(-2)}.${yyyy}`;
};

const convertDateToFullString = (value: Date) => {
    if (!(value instanceof Date) || isNaN(value.getDate())) return '';
    const pad = '00';
    const yyyy = value.getFullYear().toString();
    const MM = (value.getMonth() + 1).toString();
    const dd = value.getDate().toString();
    const hh = value.getHours().toString();
    const min = value.getMinutes().toString();
    return `${(pad + dd).slice(-2)}.${(pad + MM).slice(-2)}.${yyyy} klo. ${(pad + hh).slice(-2)}:${(pad + min).slice(-2)}`;
};

const CustomUserLicenseField = ({
    source = 'licenses',
    label = '',
    sortable = false,
    userGuid = '',
    schools = [],
}: Props) => {
    const record = useRecordContext();
    const lastIndex = record[source].length - 1;

    // Resolve school name from subscriber_guid when it differs from user guid
    const subscriberGuid = record?.subscription?.subscriber_guid;
    const isSchoolSubscription = subscriberGuid && subscriberGuid !== userGuid;
    const matchedSchool = isSchoolSubscription
        ? schools.find((s: any) => s.parent_guid === subscriberGuid)
        : null;
    const schoolTooltip = matchedSchool
        ? `Koulun tilaus: ${matchedSchool.name}`
        : isSchoolSubscription
          ? `Koulun tilaus: ${subscriberGuid}`
          : '';

    // record[props.source].map(l => {
    //     console.log(l);
    // });

    return (
        <TableContainer component={Paper}>
            <Table
                sx={{ minWidth: 450 }}
                size="small"
                aria-label="a dense table"
            >
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell width={150}>Luotu</TableCell>
                        <TableCell align="right" width={150}>
                            Mistä
                        </TableCell>
                        <TableCell align="right" width={150}>
                            Mihin
                        </TableCell>
                        <TableCell align="right" width={80}>
                            Tila
                        </TableCell>
                        <TableCell align="right" width={150}>
                            Tyyppi
                        </TableCell>
                        <TableCell align="right" width={80}>
                            Nro.
                        </TableCell>
                        <TableCell align="right" width={150}></TableCell>
                        {/* <TableCell align="right"></TableCell> */}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {record[source].map(row => (
                        <TableRow
                            key={row.guid}
                            sx={{
                                '&:last-child td, &:last-child th': {
                                    border: 0,
                                },
                            }}
                        >
                            <TableCell align="left">
                                {row.discontinued && row.discontinued !== '' ? (
                                    <Tooltip
                                        title={`Lisenssi keskeytetty: ${convertDateToFullString(new Date(row.discontinued))}`}
                                    >
                                        <Chip label="K"></Chip>
                                    </Tooltip>
                                ) : (
                                    ''
                                )}
                                {row.returned && row.returned !== '' ? (
                                    <Tooltip
                                        title={`Lisenssi palautettu: ${convertDateToFullString(new Date(row.returned))}`}
                                    >
                                        <Chip label="P"></Chip>
                                    </Tooltip>
                                ) : (
                                    ''
                                )}
                                {row.extended ? (
                                    <Tooltip title={`Lisenssi Jatkettu`}>
                                        <Chip label="J"></Chip>
                                    </Tooltip>
                                ) : (
                                    ''
                                )}
                                {row.changed && row.changed !== '' ? (
                                    <Tooltip
                                        title={`Lisenssi vaihdettu: ${convertDateToFullString(new Date(row.changed))}`}
                                    >
                                        <Chip label="V"></Chip>
                                    </Tooltip>
                                ) : (
                                    ''
                                )}
                                {row.discontinued &&
                                row.discontinued === '' &&
                                row.returned &&
                                row.returned === '' &&
                                row.changed &&
                                row.changed === '' &&
                                !row.extended ? (
                                    <Chip
                                        label=""
                                        style={{ backgroundColor: 'white' }}
                                    ></Chip>
                                ) : (
                                    ''
                                )}
                            </TableCell>
                            <TableCell component="th" scope="row">
                                <Tooltip
                                    title={convertDateToFullString(
                                        new Date(row.created)
                                    )}
                                >
                                    <span>
                                        {convertDateToString(
                                            new Date(row.created)
                                        )}
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                                <Tooltip
                                    title={convertDateToFullString(
                                        new Date(row.valid_from)
                                    )}
                                >
                                    <span>
                                        {convertDateToString(
                                            new Date(row.valid_from)
                                        )}
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                                <Tooltip
                                    title={convertDateToFullString(
                                        new Date(row.valid_until)
                                    )}
                                >
                                    <span>
                                        {convertDateToString(
                                            new Date(row.valid_until)
                                        )}
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                                <Tooltip
                                    title={`Lisenssin tila. Punainen = poistettu, Vihreä = Käytössä`}
                                >
                                    <span>
                                        <CustomStatusField
                                            url="extralist/userlicense/statuses"
                                            value={row.status}
                                        />
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                                {schoolTooltip ? (
                                    <Tooltip title={schoolTooltip}>
                                        <span>
                                            <CustomStatusField
                                                url="extralist/subscription/types"
                                                value={row.type}
                                                useIcon={false}
                                            />
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <CustomStatusField
                                        url="extralist/subscription/types"
                                        value={row.type}
                                        useIcon={false}
                                    />
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {row.reduce && (
                                    <Tooltip
                                        title={`Lisenssi on annettu lisenssipoolista järjestysnumerolla ${row.license_order_nro}`}
                                    >
                                        <span>{row.license_order_nro}</span>
                                    </Tooltip>
                                )}
                            </TableCell>
                            <TableCell align="right">
                                <Tooltip
                                    title={`Tämä toiminto poistaa lisenssin pysyvästi tietokannasta. Käytä vain, jos tiedät mitä teet!`}
                                >
                                    <span>
                                        {row.order_index == lastIndex &&
                                        row.status == 1 ? (
                                            <CustomDBDeleteButton
                                                resource_guid={row.guid}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </span>
                                </Tooltip>
                                {row.discontinued && row.discontinued !== '' ? (
                                    <Tooltip
                                        title={
                                            row.discontinued_tilausnumero
                                                ? `Lisenssi keskeytetty tilaukseen ${row.discontinued_tilausnumero}. Palauta aktiiviseksi.`
                                                : `Palauta keskeytetty lisenssi aktiiviseksi.`
                                        }
                                    >
                                        <span>
                                            <CustomDBDiscontinueLicenseButton
                                                resource_guid={row.guid}
                                                mode="restore"
                                            />
                                        </span>
                                    </Tooltip>
                                ) : row.possible_orders &&
                                  row.possible_orders.length > 0 ? (
                                    <CustomDiscontinueLicenseMenu
                                        resource_guid={row.guid}
                                        possible_orders={row.possible_orders}
                                    />
                                ) : (
                                    ''
                                )}
                            </TableCell>
                            {/* <TableCell align="right">
                        {row.discontinued!==''?<Tooltip title={`Lisenssi keskeytetty: ${convertDateToFullString(new Date(row.discontinued))}`}><span>Keskeytetty</span></Tooltip>:''}
                        {row.returned!==''?<Tooltip title={`Lisenssi palautettu: ${convertDateToFullString(new Date(row.returned))}`}><span>Palautettu</span></Tooltip>:''}
                    </TableCell> */}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CustomUserLicenseField;
